/**
 * Status-Aware Microcopy Helper Utility
 *
 * Provides shared, status-aware microcopy (helper text, explanations, empty states,
 * success banners, and action labels) across Dashboard, Task Queue, Compliance,
 * Evidence, References, and Training workflows.
 *
 * Supported statuses per domain:
 *   missing, submitted, awaiting_review, accepted, rejected,
 *   replacement_requested, expired, uploaded_in_error
 */

const _normalise = (input) => String(input || '').toLowerCase().trim().replace(/[\s-]+/g, '_');

/**
 * Get status-aware microcopy for document evidence, compliance checks, references, or training.
 *
 * @param {'document' | 'right_to_work' | 'rtw' | 'dbs' | 'identity' | 'proof_of_address' | 'training' | 'reference'} domain
 * @param {string} status
 * @param {Object} [options]
 * @param {boolean} [options.hasApplicantEvidence]
 * @param {boolean} [options.hasFormalCheck]      — true only when backend confirms a proof-of-check record exists
 * @param {boolean} [options.isExternalReplacement]
 * @returns {string}
 */
export function getStatusMicrocopy(domain, status, options = {}) {
  const normStatus = _normalise(status);
  const { hasApplicantEvidence = false, hasFormalCheck = false, isExternalReplacement = false } = options;

  const isRTW = domain === 'right_to_work' || domain === 'rtw';

  // ── Right to Work ────────────────────────────────────────────────
  if (isRTW) {
    if (normStatus === 'rejected') {
      return 'Right to Work evidence was rejected. Request a replacement or obtain new evidence.';
    }
    if (normStatus === 'replacement_requested') {
      return 'A replacement Right to Work document has been requested from the applicant.';
    }
    if (normStatus === 'expired') {
      return 'Right to Work evidence has expired. A renewed document or re-check is required.';
    }
    if (normStatus === 'uploaded_in_error') {
      return 'This Right to Work evidence was uploaded in error and has been removed from active records.';
    }

    // Formal check completed — only claim proof retained when backend confirms it
    if (hasFormalCheck && (normStatus === 'verified' || normStatus === 'complete' || normStatus === 'approved' || normStatus === 'accepted')) {
      return 'Right to Work formal check completed and verified proof retained.';
    }

    // Applicant evidence present but formal check not done yet
    // 'submitted', 'awaiting_review', 'accepted' inherently mean evidence exists;
    // 'pending' only counts when the caller confirms evidence is present
    if (
      normStatus === 'submitted' || normStatus === 'pending_review' ||
      normStatus === 'awaiting_review' || normStatus === 'accepted' ||
      hasApplicantEvidence
    ) {
      return 'Applicant evidence is available. Complete the employer Right to Work check and retain proof of the result.';
    }

    // Missing
    return 'Waiting for Right to Work evidence.';
  }

  // ── DBS ──────────────────────────────────────────────────────────
  if (domain === 'dbs') {
    if (normStatus === 'rejected') {
      return 'DBS evidence was rejected. Request a replacement or obtain new evidence.';
    }
    if (normStatus === 'replacement_requested') {
      return 'A replacement DBS certificate has been requested from the applicant.';
    }
    if (normStatus === 'expired') {
      return 'DBS evidence has expired. A renewed certificate or re-check is required.';
    }
    if (normStatus === 'uploaded_in_error') {
      return 'This DBS evidence was uploaded in error and has been removed from active records.';
    }

    // Formal check completed — only claim proof retained when backend confirms it
    if (hasFormalCheck && (normStatus === 'verified' || normStatus === 'complete' || normStatus === 'approved' || normStatus === 'accepted')) {
      return 'DBS check completed and verified proof retained.';
    }

    // Applicant evidence present but formal check not done yet
    if (
      normStatus === 'submitted' || normStatus === 'pending_review' ||
      normStatus === 'awaiting_review' || normStatus === 'accepted' ||
      hasApplicantEvidence
    ) {
      return 'Applicant DBS details available. Complete the official check and record the result.';
    }

    // Missing
    return 'Waiting for DBS evidence.';
  }

  // ── Identity ─────────────────────────────────────────────────────
  if (domain === 'identity') {
    if (normStatus === 'rejected') {
      return 'Identity document was rejected. Request a replacement from the applicant.';
    }
    if (normStatus === 'replacement_requested' || normStatus === 'redo_requested' || normStatus === 'needs_redo') {
      return 'A replacement identity document has been requested from the applicant.';
    }
    if (normStatus === 'expired') {
      return 'Identity document has expired. A renewed document is required.';
    }
    if (normStatus === 'uploaded_in_error') {
      return 'This identity document was uploaded in error and has been removed from active records.';
    }
    if (normStatus === 'approved' || normStatus === 'verified' || normStatus === 'complete' || normStatus === 'completed') {
      return 'Identity document has been reviewed and confirmed. No further action is currently required.';
    }
    if (normStatus === 'pending' || normStatus === 'submitted' || normStatus === 'awaiting_review' || normStatus === 'awaiting_admin_review' || normStatus === 'accepted') {
      return 'Identity document uploaded. Check the name, date of birth and original document before approval.';
    }

    // Missing
    return 'Awaiting identity document upload from the applicant.';
  }

  // ── Proof of Address ─────────────────────────────────────────────
  if (domain === 'proof_of_address') {
    if (normStatus === 'rejected') {
      return 'Proof of address was rejected. Request a replacement from the applicant.';
    }
    if (normStatus === 'replacement_requested' || normStatus === 'redo_requested' || normStatus === 'needs_redo') {
      return 'A replacement proof of address has been requested from the applicant.';
    }
    if (normStatus === 'expired') {
      return 'Proof of address has expired. A document dated within the last 12 months is required.';
    }
    if (normStatus === 'uploaded_in_error') {
      return 'This proof of address was uploaded in error and has been removed from active records.';
    }
    if (normStatus === 'approved' || normStatus === 'verified' || normStatus === 'complete' || normStatus === 'completed') {
      return 'Proof of address has been reviewed and confirmed. No further action is currently required.';
    }
    if (normStatus === 'pending' || normStatus === 'submitted' || normStatus === 'awaiting_review' || normStatus === 'awaiting_admin_review' || normStatus === 'accepted') {
      return 'Address evidence uploaded. Check the name, address and document date before approval.';
    }

    // Missing
    return 'Awaiting proof of address upload from the applicant.';
  }

  // ── Training ─────────────────────────────────────────────────────
  if (domain === 'training') {
    if (normStatus === 'rejected') {
      return 'Training submission was rejected. The applicant should re-submit or re-take the course.';
    }
    if (normStatus === 'replacement_requested' || normStatus === 'redo_requested' || normStatus === 'needs_redo') {
      return 'A replacement training certificate has been requested from the applicant.';
    }
    if (isExternalReplacement || normStatus === 'external_accepted' || normStatus === 'external_replaced') {
      return 'External training accepted in place of the in-system course. Review its expiry date and verification record.';
    }
    if (normStatus === 'verified' || normStatus === 'approved' || normStatus === 'complete' || normStatus === 'completed') {
      return 'Training completed and verified. No further action is currently required.';
    }
    if (normStatus === 'pending' || normStatus === 'awaiting_review' || normStatus === 'submitted') {
      return 'Training completion or certificate submitted. Waiting for admin review.';
    }
    if (normStatus === 'expired') {
      return 'Training certificate has expired. A renewal certificate or re-take is required.';
    }
    if (normStatus === 'uploaded_in_error') {
      return 'This training record was uploaded in error and has been removed from active records.';
    }

    // Missing
    return 'Training pending completion or evidence verification.';
  }

  // ── Reference ────────────────────────────────────────────────────
  if (domain === 'reference') {
    if (normStatus === 'satisfactory' || normStatus === 'approved' || normStatus === 'complete' || normStatus === 'completed' || normStatus === 'verified') {
      return 'Reference check completed and marked satisfactory. No further action is currently required.';
    }
    if (normStatus === 'unsatisfactory' || normStatus === 'rejected') {
      return 'Reference check marked unsatisfactory.';
    }
    if (normStatus === 'replacement_requested' || normStatus === 'redo_requested' || normStatus === 'needs_redo') {
      return 'A replacement reference has been requested.';
    }
    if (normStatus === 'requested' || normStatus === 'pending_response' || normStatus === 'awaiting_response' || normStatus === 'sent') {
      return 'Reference request sent. Waiting for referee response.';
    }
    if (normStatus === 'expired') {
      return 'Reference response has not been received. A follow-up or re-request is required.';
    }
    if (normStatus === 'uploaded_in_error') {
      return 'This reference was recorded in error and has been removed from active records.';
    }

    // Missing / pending
    return 'Reference request pending.';
  }

  // ── General / Document domain (fallback) ─────────────────────────
  if (normStatus === 'rejected') {
    return 'Evidence was rejected. Request a replacement from the applicant.';
  }
  if (normStatus === 'replacement_requested' || normStatus === 'redo_requested' || normStatus === 'needs_redo') {
    return 'A replacement has been requested from the applicant.';
  }
  if (normStatus === 'approved' || normStatus === 'verified' || normStatus === 'complete' || normStatus === 'completed') {
    return 'Document checked. No further action is currently required.';
  }
  if (normStatus === 'pending' || normStatus === 'submitted' || normStatus === 'awaiting_review' || normStatus === 'awaiting_admin_review') {
    return 'Applicant evidence received. Waiting for admin review.';
  }
  if (normStatus === 'expired') {
    return 'Document has expired. A renewed document is required.';
  }
  if (normStatus === 'uploaded_in_error') {
    return 'This document was uploaded in error and has been removed from active records.';
  }

  // Missing
  return 'Awaiting required evidence or review.';
}

/**
 * Helper to get clean button label based on destination/action.
 *
 * @param {'view_profile' | 'record_check' | 'request_replacement' | 'review_evidence' | 'reopen_review'} actionType
 * @param {Object} [opts]
 * @returns {string}
 */
export function getActionButtonLabel(actionType, opts = {}) {
  switch (actionType) {
    case 'view_profile':
      return opts.applicantName ? `View ${opts.applicantName}'s Profile` : 'View Employee Profile';
    case 'record_check':
      return 'Record Formal Check';
    case 'request_replacement':
      return 'Request Replacement';
    case 'review_evidence':
      return 'Review Submitted Evidence';
    case 'reopen_review':
      return 'Reopen Review';
    default:
      return 'Take Action';
  }
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    getStatusMicrocopy,
    getActionButtonLabel
  };
}
